import { Injectable, inject } from '@angular/core';
import { DialogService } from '@next/core-lib/dialog';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { OfferService } from 'src/app/shared/services/offers/offers.service';
import { RequestService } from 'src/app/shared/services/request.service';

import { OffersOverviewSlideComponent } from '../../../overview-slide-in/offers/offers-overview-slide/offers-overview-slide.component';
import { RequestsOverviewSlideComponent } from '../../../overview-slide-in/requests/requests-overview-slide/requests-overview-slide.component';
import { OverviewState } from '../../models/overview.model';
import * as OverviewActions from '../actions/overview.actions';
import { selectFilters } from '../selectors/overview.selector';

@Injectable()
export class OverviewEffects {
  private readonly dialogService = inject(DialogService);
  private readonly actions$ = inject(Actions);
  private readonly offerService = inject(OfferService);
  private readonly store = inject<Store<OverviewState>>(Store);
  private readonly requestsService = inject(RequestService);

  getOffersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OverviewActions.loadOffers),
      tap(() => this.store.dispatch(OverviewActions.setOverviewOffersLoading({ loading: true }))),
      withLatestFrom(this.store.select(selectFilters)),
      switchMap(([{ paginationOptions }, filters]) =>
        this.offerService.getOffers(paginationOptions, filters).pipe(
          tap(() =>
            this.store.dispatch(OverviewActions.setOverviewOffersLoading({ loading: false })),
          ),
          map((offersList) =>
            OverviewActions.loadOffersSuccess({
              offersList: offersList.results,
              totalNumberOfOffers: offersList.metadata.totalResultCount,
            }),
          ),
          catchError((error) => {
            this.store.dispatch(OverviewActions.setOverviewRequestLoading({ loading: false }));
            return of(NotificationActions.errorSnackbar({ message: error.message }));
          }),
        ),
      ),
    ),
  );

  getRequestsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OverviewActions.loadRequests),
      tap(() => this.store.dispatch(OverviewActions.setOverviewRequestLoading({ loading: true }))),
      withLatestFrom(this.store.select(selectFilters)),
      switchMap(([{ paginationOptions }, filters]) =>
        this.requestsService.getRequests(paginationOptions, filters).pipe(
          tap(() =>
            this.store.dispatch(OverviewActions.setOverviewRequestLoading({ loading: false })),
          ),
          map((requestsList) =>
            OverviewActions.loadRequestsSuccess({
              requestsList: requestsList.results,
              totalNumberOfRequests: requestsList.metadata.totalResultCount,
            }),
          ),
          catchError((error) => {
            this.store.dispatch(OverviewActions.setOverviewRequestLoading({ loading: false }));
            return of(NotificationActions.errorSnackbar({ message: error.message }));
          }),
        ),
      ),
    ),
  );

  openOfferOverviewDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OverviewActions.openOfferOverviewDialog),
      switchMap((actionData) =>
        this.dialogService.fullscreenDialog(
          OffersOverviewSlideComponent,
          {
            selectedOffer: actionData.selectedOffer,
            cancelButton: true,
            altButton: false,
            confirmButton: false,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(OverviewActions.openOfferOverviewDialogSuccess())),
    ),
  );

  openRequestOverviewDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OverviewActions.openRequestOverviewDialog),
      switchMap((actionData) =>
        this.dialogService.fullscreenDialog(
          RequestsOverviewSlideComponent,
          {
            selectedRequest: actionData.selectedRequest,
            cancelButton: true,
            altButton: false,
            confirmButton: false,
          },
          {
            autoFocus: false,
          },
        ),
      ),
      switchMap(() => of(OverviewActions.openRequestOverviewDialogSuccess())),
    ),
  );
}
